import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { update } from '../../Actions/Cart.action';
import { OrderRow } from '../../Types/OrderRow';
import QuantityInput from '../QuantityInput/QuantityInput';
import clsx from 'clsx';
import Icon from '../Icon';
import { translate } from '../../Services/translation';
import StockStatus from '../StockStatus/StockStatus';

interface OrderRowProductCardProps {
  row: OrderRow;
  onRemoveClick?: (row: OrderRow) => void;
  className?: string;
}

const OrderRowProductCard = ({
  row,
  onRemoveClick,
  className = '',
}: OrderRowProductCardProps) => {
  const dispatch = useDispatch();

  const [abortController, setAbortController] = useState<
    AbortController | undefined
  >();

  useEffect(() => setAbortController(new AbortController()), []);

  const onQuantityChange = useCallback(
    (row: OrderRow, quantity: number) => {
      dispatch(
        update(row.rowSystemId, quantity, undefined, abortController)
      );
    },
    [abortController, dispatch]
  );

  return (
    <div
      className={clsx(
        className,
        'flex flex-col gap-x-4 border-b border-gray-100 pb-5 md:border-none md:pb-0'
      )}
    >
      <div className="flex w-full gap-4 border-gray-100  pb-5 md:border-b">
        <div className="w-[100px]">
          {row.image && (
            <a href={row.url}>
              <img
                className="w-full rounded object-cover aspect-[3/4]"
                src={row.image}
              />
            </a>
          )}
        </div>
        <div className="flex flex-1 flex-col">
          <div className="flex flex-1 flex-col">
            <div className="mb-2 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
              <span className="text-md leading-tight text-gray-500">
                {row.productType}
                {row.showArticleNumber && <>: {row.articleNumber}</>}
              </span>
              {row.stockStatus && (
                <StockStatus {...row.stockStatus} className="!mb-0" />
              )}
            </div>
            <h4 className="mb-1.5 text-h4">
              <a href={row.url}>{row.name}</a>
            </h4>
            <div className="mb-[6px] flex flex-col gap-y-[6px]">
              {row.extraInfo.items.map((item, _) => (
                <div
                  key={row.rowSystemId + '-extra-' + item.key}
                  className="text-md font-normal leading-tight text-gray-500"
                >
                  {item.key}: {item.value}
                </div>
              ))}
              {row.previewUrl && (
                <a
                  className="text-md font-normal leading-tight text-gray-500 underline decoration-gray-500"
                  href={row.previewUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {translate('order-row.see-preview')}
                </a>
              )}
            </div>
            <div
              className={clsx(
                'mb-2 flex flex-col text-md font-normal leading-tight text-gray-500 lg:block',
                row.campaignPrice && '!text-campaign'
              )}
            >
              {row.formattedUnitCampaignPrice ?? row.formattedUnitPrice}
              {row.unitOfMeasurement && (
                <span className="text-gray-700">
                  {' / ' + row.unitOfMeasurement.toLowerCase()}
                </span>
              )}
              {row.campaignPrice && (
                <span className="mt-2 text-sm font-light text-gray-400 lg:ml-2 lg:mt-0">
                  {translate('cart-item.regular-unit-price')}{' '}
                  {row.formattedUnitPrice}
                </span>
              )}
            </div>
          </div>
          <div className="hidden items-center justify-between md:flex">
            <div className="flex flex-wrap items-center">
              <QuantityInput
                quantity={row.quantity}
                showButtons={!row.isFreeGift}
                disabled={row.isFreeGift || !row.canEditQuantity}
                mediumSize={true}
                wrapperClassName="max-w-[112px]"
                onChange={(quantity) => onQuantityChange(row, quantity)}
              />
              {onRemoveClick && !row.isFreeGift && (
                <button
                  className="color-gray-500 ml-2 cursor-pointer p-2"
                  onClick={() => onRemoveClick && onRemoveClick(row)}
                >
                  <Icon name="delete" className="h-5 w-5" />
                </button>
              )}
            </div>
            <div className="text-right text-lg font-normal">
              {row.campaignPrice && row.campaignPrice != '' ? (
                <span className="text-campaign">{row.campaignPrice}</span>
              ) : (
                row.price
              )}
              {row.campaignPrice && row.campaignPrice != '' && (
                <div className="text-sm text-gray-500 line-through">
                  {row.price}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between md:hidden">
        <div className="flex flex-wrap items-center">
          <QuantityInput
            quantity={row.quantity}
            showButtons={!row.isFreeGift}
            disabled={row.isFreeGift || !row.canEditQuantity}
            mediumSize={true}
            wrapperClassName="max-w-[112px]"
            onChange={(quantity) => onQuantityChange(row, quantity)}
          />
          {onRemoveClick && !row.isFreeGift && (
            <button
              className="color-gray-500 ml-2 cursor-pointer p-2"
              onClick={() => onRemoveClick && onRemoveClick(row)}
            >
              <Icon name="delete" className="h-5 w-5" />
            </button>
          )}
        </div>
        <div className="text-right text-lg font-normal">
          {row.campaignPrice && row.campaignPrice != ''
            ? row.campaignPrice
            : row.price}
          {row.campaignPrice && row.campaignPrice != '' && (
            <div className="text-sm text-gray-500 line-through">
              {row.price}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderRowProductCard;
